import React, { useId } from "react";
import { useTranslation } from "react-i18next";
import { Button, ButtonProps } from "../Button";
import { useCallback } from "react";
import { TranslateEvent } from "ol/interaction/Translate";
import { MovementIcon } from "../../../constants/icons/MovementIcon";
import { useFeatureStore, useMap } from "../../../hooks";
import { useControlSection } from "../../layout";
import { InnerButton } from "../InnerButton";
import { useFeaturesStore } from "../../../FeaturesStore";
import { useFeatureFinder } from "../../../hooks/incontext/useFeatureFinder";
import { useTranslateInteraction } from "../../../hooks/incontext/useTranslateInteraction";
import { useReleaseToolWithoutSelection } from "src/domains/shared/react-openlayers/Map/hooks/incontext/useReleaseToolWithoutSelection";
import { useTranslateHandleSync } from "../../../hooks/incontext/useTranslateHandleSync";
import { resolveTranslatedFeatureUpdate } from "../../../utils/geometry";

import { MAP_TOOL_BUTTON } from "src/domains/shared/constants/mapToolButton";

export interface MoveAnnotationProps extends ButtonProps {
  target: any | null;
  onMoveChange?: (e: TranslateEvent) => void;
}

export const MoveAnnotation = ({
  target,
  onMoveChange,
  disabled = false,
  ...props
}: MoveAnnotationProps) => {
  const { t } = useTranslation("timeSeries");
  const { selectedFeature } = useFeatureStore();
  const { geoJson, updateGeoJson, getGeoJsonElement } = useFeaturesStore();
  const map = useMap();
  const id = useId();
  const buttonId = `controlbutton-${id}`;
  const { selectButton, selectedButtonId } = useControlSection();
  const isActive = buttonId === selectedButtonId;
  const { findFeatureById } = useFeatureFinder();

  const onTranslateEnd = useCallback(
    (event: TranslateEvent) => {
      const result = resolveTranslatedFeatureUpdate(event);
      if (!result) return;
      onMoveChange?.(event);
      updateGeoJson(result.id, result.geoJson);
    },
    [updateGeoJson, onMoveChange]
  );

  const { install: installTranslate, remove: removeTranslate } =
    useTranslateInteraction(map, onTranslateEnd);

  useTranslateHandleSync({
    isActive,
    geoJson,
    selectedFeature,
    findFeatureById,
    installTranslate,
    removeTranslate,
  });

  useReleaseToolWithoutSelection({
    isActive,
    hasSelection: Boolean(selectedFeature?.id),
    release: () => selectButton(""),
  });

  const handleButtonClick = useCallback(() => {
    if (isActive) {
      selectButton("");
    } else {
      if (!selectedFeature?.id) return;
      selectButton(buttonId);
      const fid = String(selectedFeature.id);
      const cur = getGeoJsonElement(fid) as any;
      if (cur) {
        updateGeoJson(fid, {
          ...cur,
          properties: { ...(cur.properties || {}), isMoving: true },
        } as any);
      }
    }
  }, [isActive, selectedFeature, selectButton, buttonId, getGeoJsonElement, updateGeoJson]);

  const isButtonDisabled = disabled || !selectedFeature?.id;

  return (
    <Button
      id={buttonId}
      isDisabled={isButtonDisabled}
      disabled={isButtonDisabled}
      hasPopup
      popupText={t("mapTool.tool.move")}
      onClick={handleButtonClick}
      isActive={isActive}
      aria-pressed={isActive}
      {...props}
    >
      <InnerButton isActive={isActive}>
        <MovementIcon
          size={MAP_TOOL_BUTTON.ICON_PX}
          color={isActive ? MAP_TOOL_BUTTON.activeIcon : MAP_TOOL_BUTTON.idleIcon}
        />
      </InnerButton>
    </Button>
  );
};
